import { Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { initials, useSession, userColor, type ThemePref } from "../lib/app";
import { useAvatarUrl } from "../lib/avatars";

export function Modal({
  title,
  onClose,
  children,
  footer,
  width = 480,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    const fn = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ width }} role="dialog" aria-modal="true">
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/** Someone's profile picture, or their initials on their colour when they have none (or while it loads). `image` overrides the picture. */
export function Avatar({ id, name, size = 26, ring, title, image }: { id: number | string; name: string; size?: number; ring?: boolean; title?: string; image?: string }) {
  const picture = useAvatarUrl(id);
  const src = image ?? picture;
  const [broken, setBroken] = useState<string | null>(null);
  return (
    <span
      className={`avatar${ring ? " avatar-ring" : ""}`}
      title={title ?? name}
      style={{ width: size, height: size, fontSize: size * 0.4, background: userColor(id) }}
    >
      {initials(name)}
      {src && src !== broken && <img src={src} alt="" decoding="async" draggable={false} onError={() => setBroken(src)} />}
    </span>
  );
}

export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  hint?: string;
  /** Tooltip saying what the item does. */
  title?: string;
}

/** A small context menu rendered at a fixed position. */
export function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: Array<MenuItem | "-">; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  useEffect(() => {
    const el = ref.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setPos({ x: Math.min(x, innerWidth - r.width - 8), y: Math.min(y, innerHeight - r.height - 8) });
    }
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      if (e instanceof MouseEvent && ref.current?.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener("mousedown", close, true);
    window.addEventListener("keydown", close);
    window.addEventListener("blur", onClose);
    return () => {
      window.removeEventListener("mousedown", close, true);
      window.removeEventListener("keydown", close);
      window.removeEventListener("blur", onClose);
    };
  }, [x, y, onClose]);
  return createPortal(
    <div className="ctx-menu" ref={ref} style={{ left: pos.x, top: pos.y }} role="menu">
      {items.map((item, i) =>
        item === "-" ? (
          <div key={i} className="ctx-sep" />
        ) : (
          <button
            key={i}
            role="menuitem"
            className={item.danger ? "danger" : ""}
            disabled={item.disabled}
            title={item.title}
            onClick={() => {
              onClose();
              item.onSelect();
            }}
          >
            <span>{item.label}</span>
            {item.hint && <kbd>{item.hint}</kbd>}
          </button>
        ),
      )}
    </div>,
    document.body,
  );
}

/** Draggable divider between two panes. Calls onDrag with the pointer delta in px, and the pointer's position. */
export function Splitter({
  onDrag,
  onDragStart,
  onDragEnd,
  vertical = true,
  onDoubleClick,
  className,
  children,
}: {
  onDrag: (delta: number, pos: number) => void;
  onDragStart?: (pos: number) => void;
  onDragEnd?: () => void;
  vertical?: boolean;
  onDoubleClick?: () => void;
  className?: string;
  /** Controls that sit on the divider; clicking them doesn't start a drag. */
  children?: ReactNode;
}) {
  // The move listener lives for the whole drag, so always call the latest handlers.
  const handlers = useRef({ onDrag, onDragEnd });
  handlers.current = { onDrag, onDragEnd };
  return (
    <div
      className={`splitter ${vertical ? "splitter-v" : "splitter-h"}${className ? ` ${className}` : ""}`}
      onDoubleClick={onDoubleClick}
      onPointerDown={(e) => {
        e.preventDefault();
        const el = e.currentTarget;
        el.setPointerCapture(e.pointerId);
        let last = vertical ? e.clientX : e.clientY;
        document.body.classList.add("dragging");
        onDragStart?.(last);
        const move = (ev: PointerEvent) => {
          const cur = vertical ? ev.clientX : ev.clientY;
          handlers.current.onDrag(cur - last, cur);
          last = cur;
        };
        const up = () => {
          document.body.classList.remove("dragging");
          el.removeEventListener("pointermove", move);
          el.removeEventListener("pointerup", up);
          el.removeEventListener("pointercancel", up);
          handlers.current.onDragEnd?.();
        };
        el.addEventListener("pointermove", move);
        el.addEventListener("pointerup", up);
        el.addEventListener("pointercancel", up);
      }}
    >
      {children && (
        <div className="splitter-actions" onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
    </div>
  );
}

export function Spinner({ size = 14 }: { size?: number }) {
  return <span className="spinner" style={{ width: size, height: size }} aria-label="Loading" />;
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function CopyBox({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="copybox">
      <input readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
      <button
        className="btn"
        onClick={async () => {
          await navigator.clipboard.writeText(value).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export function Leaf({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" className="leaf-mark">
      <path d="M7 25c0-9.5 6-16 18-16.6C24.4 19.6 17.5 25 7 25z" fill="currentColor" />
      <path d="M7 25c4.3-4.6 8-7.8 12.6-10.8" stroke="var(--leaf-vein, var(--bg-raised))" strokeWidth="1.7" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/** A page with a moon on it, for the dark PDF toggle (a bare moon would read as the app's own dark theme). */
export function DarkPageIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M12 9a3.7 3.7 0 0 0 5.5 5.5 5.5 5.5 0 1 1-5.5-5.5Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

const THEMES: Array<[ThemePref, typeof Sun, string]> = [
  ["light", Sun, "Light"],
  ["dark", Moon, "Dark"],
  ["system", Monitor, "Match system"],
];

export function ThemeSwitch({ className = "" }: { className?: string }) {
  const { theme, setTheme } = useSession();
  return (
    <div className={`theme-switch ${className}`} role="radiogroup" aria-label="Theme">
      {THEMES.map(([value, Icon, label]) => (
        <button key={value} type="button" role="radio" aria-checked={theme === value} className={theme === value ? "on" : ""} title={label} onClick={() => setTheme(value)}>
          <Icon size={14} strokeWidth={2} />
        </button>
      ))}
    </div>
  );
}
